import React, { useState } from 'react';
import { Globe, Mail, Phone, MapPin, Factory, Send, ArrowRight, CheckCircle, Menu, X, Search, FileCheck, Truck, ShieldCheck, DollarSign, Star, Quote } from 'lucide-react';
import axios from 'axios';

const categoryImages = {
  food: '/img/alimentaires.jpeg',
  hvac: '/img/climatisations.jpeg',
  energy: '/img/energies-renouvelables.jpeg',
  appliances: '/img/electromenager.jpeg',
  it: '/img/informatiques.jpeg',
  beauty: '/img/hygienes.jpeg',
  tableware: '/img/art-table.jpeg',
  textile: '/img/textile.jpeg',
  bazar: '/img/bazar.jpeg',
  building: '/img/batiment.jpeg',
  toys: '/img/jouet.jpeg',
  tools: '/img/outillage.jpeg'
};

const translations = {
  en: {
    nav: {
      home: 'Home',
      about: 'About',
      process: 'Process',
      categories: 'Categories',
      contact: 'Contact'
    },
    hero: {
      title: 'Trade4All',
      subtitle: 'Global Product Sourcing Excellence',
      description: 'Connecting businesses with premium manufacturers across China, Vietnam, India & Eastern Europe',
      cta: 'Start Sourcing',
      scroll: 'Explore Our Services'
    },
    about: {
      title: 'Your Trusted Sourcing Partner',
      description: 'We bridge the gap between your business and world-class manufacturers. With deep industry expertise and an extensive global network, we deliver quality products that meet your exact specifications.',
      experience: 'Trusted by Industry Leaders',
      brands: 'Including Nutella, Coca-Cola & Nestlé',
      network: 'Global Network',
      locations: ['China', 'Vietnam', 'India', 'Eastern Europe']
    },
    credibility: {
      title: 'Why Choose Us',
      subtitle: 'Your success is our priority',
      items: [
        { title: 'Optimized Prices', description: 'Best market rates with transparent pricing' },
        { title: 'Verified Suppliers', description: 'Thoroughly vetted manufacturing partners' },
        { title: 'Quality Controlled', description: 'Rigorous inspection at every stage' },
        { title: 'Secure Delivery', description: 'On-time delivery guaranteed' }
      ]
    },
    process: {
      title: 'How It Works',
      subtitle: 'Our simple and efficient sourcing process',
      steps: [
        { title: 'Consultation', description: 'Tell us your product requirements and specifications' },
        { title: 'Sourcing', description: 'We identify and vet the best manufacturers for your needs' },
        { title: 'Quality Check', description: 'Rigorous inspection and quality control process' },
        { title: 'Delivery', description: 'Secure logistics and on-time delivery to your location' }
      ]
    },
    reviews: {
      title: 'What Our Clients Say',
      subtitle: 'Trusted by businesses worldwide',
      items: [
        { name: 'Sarah Johnson', company: 'Global Retail Corp', text: 'Trade4All transformed our sourcing process. Their attention to detail and commitment to quality is unmatched. We\'ve reduced costs by 30% while improving product quality.' },
        { name: 'Michel Dubois', company: 'European Distributors', text: 'Professional, reliable, and efficient. They handle everything from sourcing to delivery. Their network in Asia is impressive and they always deliver on time.' },
        { name: 'James Chen', company: 'Tech Solutions Ltd', text: 'Outstanding service! They found us the perfect manufacturers in China and Vietnam. The quality control process gave us complete peace of mind.' }
      ]
    },
    categories: {
      title: 'Product Categories',
      subtitle: 'Comprehensive sourcing solutions across diverse industries',
      items: {
        food: 'Food & Beverage',
        hvac: 'Air Conditioning',
        energy: 'Renewable Energy',
        appliances: 'Home Appliances',
        it: 'IT & Electronics',
        beauty: 'Hygiene & Beauty',
        tableware: 'Tableware',
        textile: 'Textile',
        bazar: 'General Merchandise',
        building: 'Construction Materials',
        toys: 'Toys',
        tools: 'Tools & Hardware'
      },
      cta: 'Get Quote'
    },
    contact: {
      title: 'Let\'s Work Together',
      subtitle: 'Tell us about your sourcing needs',
      form: {
        name: 'Full Name',
        email: 'Email Address',
        phone: 'Phone Number',
        company: 'Company Name',
        category: 'Product Category',
        message: 'Tell us about your requirements',
        submit: 'Send Message',
        sending: 'Sending...',
        success: 'Thank you! We\'ll be in touch shortly.',
        error: 'Something went wrong. Please try again.'
      }
    },
    footer: {
      rights: 'All rights reserved.',
      tagline: 'Premium Product Sourcing Solutions'
    }
  },
  fr: {
    nav: {
      home: 'Accueil',
      about: 'À Propos',
      process: 'Processus',
      categories: 'Catégories',
      contact: 'Contact'
    },
    hero: {
      title: 'Trade4All',
      subtitle: 'Excellence en Sourcing de Produits Mondial',
      description: 'Connecter les entreprises avec des fabricants premium en Chine, Vietnam, Inde et Europe de l\'Est',
      cta: 'Commencer',
      scroll: 'Découvrir Nos Services'
    },
    about: {
      title: 'Votre Partenaire de Sourcing de Confiance',
      description: 'Nous comblons le fossé entre votre entreprise et les fabricants de classe mondiale. Avec une expertise approfondie et un vaste réseau mondial, nous livrons des produits de qualité qui répondent à vos spécifications exactes.',
      experience: 'Approuvé par les Leaders de l\'Industrie',
      brands: 'Y compris Nutella, Coca-Cola & Nestlé',
      network: 'Réseau Mondial',
      locations: ['Chine', 'Vietnam', 'Inde', 'Europe de l\'Est']
    },
    credibility: {
      title: 'Pourquoi Nous Choisir',
      subtitle: 'Votre succès est notre priorité',
      items: [
        { title: 'Prix Optimisés', description: 'Meilleurs tarifs du marché avec transparence totale' },
        { title: 'Fournisseurs Vérifiés', description: 'Partenaires de fabrication rigoureusement contrôlés' },
        { title: 'Qualité Contrôlée', description: 'Inspection rigoureuse à chaque étape' },
        { title: 'Délais Sécurisés', description: 'Livraison à temps garantie' }
      ]
    },
    process: {
      title: 'Comment Ça Marche',
      subtitle: 'Notre processus de sourcing simple et efficace',
      steps: [
        { title: 'Consultation', description: 'Parlez-nous de vos besoins et spécifications produits' },
        { title: 'Sourcing', description: 'Nous identifions les meilleurs fabricants pour vos besoins' },
        { title: 'Contrôle Qualité', description: 'Processus rigoureux d\'inspection et de contrôle qualité' },
        { title: 'Livraison', description: 'Logistique sécurisée et livraison à temps à votre emplacement' }
      ]
    },
    reviews: {
      title: 'Ce Que Disent Nos Clients',
      subtitle: 'Approuvé par des entreprises du monde entier',
      items: [
        { name: 'Sarah Johnson', company: 'Global Retail Corp', text: 'Trade4All a transformé notre processus de sourcing. Leur attention aux détails et engagement envers la qualité sont inégalés. Nous avons réduit nos coûts de 30% tout en améliorant la qualité.' },
        { name: 'Michel Dubois', company: 'Distributeurs Européens', text: 'Professionnel, fiable et efficace. Ils gèrent tout du sourcing à la livraison. Leur réseau en Asie est impressionnant et ils livrent toujours à temps.' },
        { name: 'James Chen', company: 'Tech Solutions Ltd', text: 'Service exceptionnel ! Ils nous ont trouvé les fabricants parfaits en Chine et au Vietnam. Le processus de contrôle qualité nous a donné une tranquillité d\'esprit totale.' }
      ]
    },
    categories: {
      title: 'Catégories de Produits',
      subtitle: 'Solutions de sourcing complètes dans diverses industries',
      items: {
        food: 'Alimentaire',
        hvac: 'Climatisation',
        energy: 'Énergie renouvelable',
        appliances: 'Électroménager',
        it: 'Informatique',
        beauty: 'Hygiène & Beauté',
        tableware: 'Art de la table',
        textile: 'Textile',
        bazar: 'Bazar',
        building: 'Bâtiment',
        toys: 'Jouets',
        tools: 'Outillage'
      },
      cta: 'Demander un devis'
    },
    contact: {
      title: 'Travaillons Ensemble',
      subtitle: 'Parlez-nous de vos besoins en sourcing',
      form: {
        name: 'Nom Complet',
        email: 'Adresse Email',
        phone: 'Numéro de Téléphone',
        company: 'Nom de l\'Entreprise',
        category: 'Catégorie de Produit',
        message: 'Parlez-nous de vos besoins',
        submit: 'Envoyer',
        sending: 'Envoi...',
        success: 'Merci ! Nous vous contacterons bientôt.',
        error: 'Une erreur s\'est produite. Veuillez réessayer.'
      }
    },
    footer: {
      rights: 'Tous droits réservés.',
      tagline: 'Solutions Premium de Sourcing de Produits'
    }
  }
};

function App() {
  const [language, setLanguage] = useState('en');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    category: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const t = translations[language];

  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'fr' : 'en');
  };

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleCategoryClick = (category) => {
    setFormData({ ...formData, category });
    document.getElementById('contact').scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: '', message: '' });

    try {
      await axios.post('/api/contact', {
        ...formData,
        language
      });
      setFormStatus({ type: 'success', message: t.contact.form.success });
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        category: '',
        message: ''
      });
    } catch (error) {
      setFormStatus({ type: 'error', message: t.contact.form.error });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-md shadow-sm z-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center">
              <div className="w-44 h-16">
                <img 
                  src="/img/logo-trade4all.png" 
                  alt="Trade4All Logo" 
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
            
            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollToSection('home')} className="text-gray-700 hover:text-brand hover:font-semibold font-medium transition-all">
                {t.nav.home}
              </button>
              <button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-brand hover:font-semibold font-medium transition-all">
                {t.nav.about}
              </button>
              <button onClick={() => scrollToSection('process')} className="text-gray-700 hover:text-brand hover:font-semibold font-medium transition-all">
                {t.nav.process}
              </button>
              <button onClick={() => scrollToSection('categories')} className="text-gray-700 hover:text-brand hover:font-semibold font-medium transition-all">
                {t.nav.categories}
              </button>
              <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-brand hover:font-semibold font-medium transition-all">
                {t.nav.contact}
              </button>
              <button
                onClick={toggleLanguage}
                className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-gray-100 hover:bg-gray-200 transition-all font-medium"
                title={language === 'en' ? 'Switch to French' : 'Switch to English'}
              >
                <span className="text-2xl">{language === 'en' ? '🇫🇷' : '🇬🇧'}</span>
              </button>
            </div>

            <div className="md:hidden flex items-center gap-4">
              <button
                onClick={toggleLanguage}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-all font-medium"
                title={language === 'en' ? 'Switch to French' : 'Switch to English'}
              >
                <span className="text-2xl">{language === 'en' ? '🇫🇷' : '🇬🇧'}</span>
              </button>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 text-gray-700 hover:text-brand"
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>

          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-100">
              <div className="flex flex-col gap-4">
                <button onClick={() => scrollToSection('home')} className="text-left text-gray-700 hover:text-brand hover:font-semibold font-medium py-2 transition-all">
                  {t.nav.home}
                </button>
                <button onClick={() => scrollToSection('about')} className="text-left text-gray-700 hover:text-brand hover:font-semibold font-medium py-2 transition-all">
                  {t.nav.about}
                </button>
                <button onClick={() => scrollToSection('process')} className="text-left text-gray-700 hover:text-brand hover:font-semibold font-medium py-2 transition-all">
                  {t.nav.process}
                </button>
                <button onClick={() => scrollToSection('categories')} className="text-left text-gray-700 hover:text-brand hover:font-semibold font-medium py-2 transition-all">
                  {t.nav.categories}
                </button>
                <button onClick={() => scrollToSection('contact')} className="text-left text-gray-700 hover:text-brand hover:font-semibold font-medium py-2 transition-all">
                  {t.nav.contact}
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>

      <section 
        id="home"
        className="relative pt-32 pb-24 px-6 lg:px-8 overflow-hidden"
        style={{
          backgroundImage: 'url(/img/trade4all-hero.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900/50 via-brand/40 to-brand/50"></div>
        <div className="relative max-w-7xl mx-auto text-center">
          <h1 className="text-6xl md:text-7xl font-bold text-white mb-6 leading-tight">
            {t.hero.title}
          </h1>
          <p className="text-2xl md:text-3xl text-yellow-400 mb-6 font-light">
            {t.hero.subtitle}
          </p>
          <p className="text-lg text-gray-200 max-w-3xl mx-auto mb-10 leading-relaxed">
            {t.hero.description}
          </p>
          <button
            onClick={() => scrollToSection('contact')}
            className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-brand to-brand-700 text-white font-semibold rounded-full hover:shadow-2xl hover:scale-105 transition-all duration-300"
          >
            {t.hero.cta}
            <ArrowRight className="h-5 w-5" />
          </button>
        </div>
      </section>

      <section id="about" className="py-24 px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                {t.about.title}
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                {t.about.description}
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-brand flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-gray-900">{t.about.experience}</p>
                    <p className="text-gray-600">{t.about.brands}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {t.about.locations.map((location, idx) => (
                <div key={idx} className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-shadow border border-gray-100">
                  <MapPin className="h-8 w-8 text-brand mb-3" />
                  <p className="font-semibold text-gray-900">{location}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {t.credibility.title}
            </h2>
            <p className="text-lg text-gray-600">
              {t.credibility.subtitle}
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {t.credibility.items.map((item, idx) => {
              const icons = [DollarSign, ShieldCheck, FileCheck, Truck];
              const Icon = icons[idx];
              return (
                <div key={idx} className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-brand/15 to-yellow-100 rounded-2xl flex items-center justify-center">
                    <Icon className="h-8 w-8 text-brand" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section id="process" className="py-24 px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {t.process.title}
            </h2>
            <p className="text-lg text-gray-600">
              {t.process.subtitle}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {t.process.steps.map((step, idx) => {
              const icons = [Search, Factory, FileCheck, Truck];
              const Icon = icons[idx];
              return (
                <div key={idx} className="relative">
                  <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow h-full">
                    <div className="w-12 h-12 bg-gradient-to-br from-brand to-brand-700 rounded-xl flex items-center justify-center mb-4">
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <div className="absolute -top-4 -left-4 w-12 h-12 bg-brand rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg">
                      {idx + 1}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                    <p className="text-gray-600">{step.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section id="categories" className="py-24 px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {t.categories.title}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t.categories.subtitle}
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {Object.entries(t.categories.items).map(([key, value]) => (
              <button
                key={key}
                onClick={() => handleCategoryClick(value)}
                className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 aspect-square"
              >
                {categoryImages[key] ? (
                  <img 
                    src={categoryImages[key]} 
                    alt={value}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-brand to-brand-700"></div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/50 via-gray-900/20 to-transparent group-hover:from-gray-900/80 group-hover:via-gray-900/60 transition-all duration-300"></div>
                <div className="absolute inset-0 flex flex-col items-center justify-end p-6">
                  <h3 className="text-white font-bold text-lg mb-3 text-center">{value}</h3>
                  <span className="px-4 py-2 bg-yellow-500 text-gray-900 text-sm font-semibold rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:translate-y-0 translate-y-2">
                    {t.categories.cta}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {t.reviews.title}
            </h2>
            <p className="text-lg text-gray-600">
              {t.reviews.subtitle}
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {t.reviews.items.map((review, idx) => (
              <div key={idx} className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <Quote className="h-8 w-8 text-brand mb-4 opacity-50" />
                <p className="text-gray-700 mb-6 leading-relaxed italic">"{review.text}"</p>
                <div className="border-t border-gray-100 pt-4">
                  <p className="font-bold text-gray-900">{review.name}</p>
                  <p className="text-sm text-gray-600">{review.company}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="py-24 px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {t.contact.title}
            </h2>
            <p className="text-lg text-gray-600">
              {t.contact.subtitle}
            </p>
          </div>
          
          <form onSubmit={handleSubmit} className="bg-white p-8 md:p-12 rounded-3xl shadow-xl">
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-gray-700 font-semibold mb-2 text-sm">
                  {t.contact.form.name} *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 rounded-xl bg-gray-50 text-gray-900 border-2 border-gray-200 focus:border-brand outline-none transition-colors"
                />
              </div>
              <div>
                <label className="block text-gray-700 font-semibold mb-2 text-sm">
                  {t.contact.form.email} *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 rounded-xl bg-gray-50 text-gray-900 border-2 border-gray-200 focus:border-brand outline-none transition-colors"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-gray-700 font-semibold mb-2 text-sm">
                  {t.contact.form.phone}
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-xl bg-gray-50 text-gray-900 border-2 border-gray-200 focus:border-brand outline-none transition-colors"
                />
              </div>
              <div>
                <label className="block text-gray-700 font-semibold mb-2 text-sm">
                  {t.contact.form.company}
                </label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-xl bg-gray-50 text-gray-900 border-2 border-gray-200 focus:border-brand outline-none transition-colors"
                />
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-gray-700 font-semibold mb-2 text-sm">
                {t.contact.form.category}
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                className="w-full px-4 py-3 rounded-xl bg-gray-50 text-gray-900 border-2 border-gray-200 focus:border-brand outline-none transition-colors"
              >
                <option value="">Select a category</option>
                {Object.values(t.categories.items).map((category) => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>

            <div className="mb-6">
              <label className="block text-gray-700 font-semibold mb-2 text-sm">
                {t.contact.form.message} *
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                required
                rows="5"
                className="w-full px-4 py-3 rounded-xl bg-gray-50 text-gray-900 border-2 border-gray-200 focus:border-brand outline-none resize-none transition-colors"
              ></textarea>
            </div>

            {formStatus.message && (
              <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${
                formStatus.type === 'success' 
                  ? 'bg-brand/10 text-brand border border-brand/20' 
                  : 'bg-red-50 text-red-800 border border-red-200'
              }`}>
                {formStatus.type === 'success' && <CheckCircle className="h-5 w-5" />}
                {formStatus.message}
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full px-8 py-4 bg-gradient-to-r from-brand to-brand-700 text-white font-semibold rounded-xl hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <Send className="h-5 w-5" />
              {isSubmitting ? t.contact.form.sending : t.contact.form.submit}
            </button>
          </form>
        </div>
      </section>

      <footer className="bg-gray-900 py-12 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex flex-col items-center md:items-start gap-2">
              <div className="bg-white rounded-2xl p-3 w-44">
                <img 
                  src="/img/logo-trade4all.png" 
                  alt="Trade4All Logo" 
                  className="w-full h-auto object-contain"
                />
              </div>
              <p className="text-gray-400 text-sm text-center md:text-left">{t.footer.tagline}</p>
            </div>
            <div className="flex items-center gap-6 text-gray-400">
              <Mail className="h-5 w-5" />
              <Phone className="h-5 w-5" />
              <MapPin className="h-5 w-5" />
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-gray-800 text-center text-gray-400 text-sm">
            © 2024 Trade4All LTD. {t.footer.rights}
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
